import { Box, Button, Grid, TextField, Typography, IconButton, Select, MenuItem, Card, CardMedia, CardContent, CardActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontSize: '0.875rem',
    bgcolor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  '& .MuiFormHelperText-root': { fontSize: '0.75rem' },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: '0.875rem',
  bgcolor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  '& .MuiSelect-select': { padding: '8px 14px' },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  bgcolor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  p: 3,
  mb: { xs: 2, md: 0 },
}));

const PreviewCard = styled(Card)(({ theme }) => ({
  maxWidth: { xs: '100%', sm: 320 },
  width: '100%',
  margin: 'auto',
  borderRadius: 12,
  boxShadow: theme.shadows[4],
  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[8],
    transform: 'translateY(-2px)',
  },
  overflow: 'hidden',
}));

const PlaceholderImage = styled(Box)(({ theme }) => ({
  height: 180,
  backgroundColor: theme.palette.grey[200],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  padding: '6px 16px',
  borderColor: theme.palette.grey[300],
  color: theme.palette.text.primary,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
  },
  '&.Mui-disabled': {
    borderColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
}));

const DMTemplateConfiguration = ({ template: templateValue, onChange }) => {
  const { t: translate } = useTranslation();
  // The API omits empty optional template fields.
  const template = {
    ...templateValue,
    title: templateValue?.title ?? '',
    subtitle: templateValue?.subtitle ?? '',
    image_url: templateValue?.image_url ?? '',
    default_action: {
      ...templateValue?.default_action,
      type: templateValue?.default_action?.type ?? 'web_url',
      url: templateValue?.default_action?.url ?? '',
    },
    buttons: templateValue?.buttons ?? [],
  };

  const handleFieldChange = (field, value) => {
    onChange({ ...template, [field]: value });
  };

  const handleDefaultActionChange = (field, value) => {
    onChange({
      ...template,
      default_action: { ...template.default_action, [field]: value },
    });
  };

  const handleButtonChange = (index, field, value) => {
    const newButtons = [...template.buttons];
    newButtons[index] = { ...newButtons[index], [field]: value };

    // Automatically set payload to "DEVELOPER_DEFINED_PAYLOAD" if type is "postback" and title is "Start Chatting"
    if (field === 'type' && value === 'postback') {
      newButtons[index].payload = 'DEVELOPER_DEFINED_PAYLOAD';
    } else if (field === 'title' && value === 'Start Chatting' && newButtons[index].type === 'postback') {
      newButtons[index].payload = 'DEVELOPER_DEFINED_PAYLOAD';
    }

    onChange({ ...template, buttons: newButtons });
  };

  const addButton = () => {
    if (template.buttons.length >= 3) return;
    onChange({
      ...template,
      buttons: [...template.buttons, { type: 'web_url', title: '', url: '', payload: '' }],
    });
  };

  const removeButton = (index) => {
    const newButtons = template.buttons.filter((_, i) => i !== index);
    onChange({ ...template, buttons: newButtons });
  };

  const isValidImageUrl = () => {
    return template?.image_url !== undefined && template?.image_url !== '';
  };

  const renderPreview = () => {
    return (
      <PreviewCard>
        {isValidImageUrl() ? (
          <CardMedia
            component="img"
            height="180"
            image={template.image_url}
            alt={translate('dm_template_configuration.template_image_alt', 'Template image')}
            sx={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
          />
        ) : (
          <PlaceholderImage>
            <ImageIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {translate('dm_template_configuration.no_image', 'No image')}
            </Typography>
          </PlaceholderImage>
        )}
        <CardContent sx={{ p: 2.5 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: '1rem', fontWeight: 600, mb: template.subtitle ? 1 : 0, lineHeight: 1.4 }}
          >
            {template.title || translate('dm_template_configuration.enter_title', 'Enter a title')}
          </Typography>
          {template.subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
              {template.subtitle}
            </Typography>
          )}
        </CardContent>
        {template?.buttons?.length > 0 && (
          <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2.5, pt: 0 }}>
            {template?.buttons.map((button, index) => (
              <StyledButton
                key={index}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 1 }}
                disabled={!button.title}
              >
                {button.title || translate('dm_template_configuration.untitled_button', 'Untitled Button')}
              </StyledButton>
            ))}
          </CardActions>
        )}
      </PreviewCard>
    );
  };

  return (
    <Box>
      <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
        {translate('dm_template_configuration.generic_template_configuration', 'Generic Template Configuration')}
      </Typography>
      <Grid container spacing={2} alignItems="stretch">
        <Grid item lg={12} xl={6}>
          <FormContainer>
            <Grid container spacing={2.5} sx={{ p: 1 }}>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label={translate('dm_template_configuration.title_required', 'Title (Required)')}
                  value={template.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder={translate('dm_template_configuration.enter_template_title', 'Enter template title (max 80 characters)')}
                  inputProps={{ maxLength: 80 }}
                  error={!template.title.trim()}
                  helperText={!template.title.trim() ? translate('dm_template_configuration.title_is_required', 'Title is required') : `${template.title.length}/80`}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label={translate('dm_template_configuration.subtitle', 'Subtitle')}
                  value={template.subtitle}
                  onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                  placeholder={translate('dm_template_configuration.enter_subtitle', 'Enter subtitle (max 80 characters)')}
                  inputProps={{ maxLength: 80 }}
                  helperText={`${template.subtitle.length}/80`}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label={translate('dm_template_configuration.image_url', 'Image URL')}
                  value={template.image_url}
                  onChange={(e) => handleFieldChange('image_url', e.target.value)}
                  placeholder={translate('dm_template_configuration.enter_image_url', 'Enter image URL (e.g., https://example.com/image.jpg)')}
                  type="url"
                  helperText={translate('dm_template_configuration.supports_jpg_png_gif', 'Supports JPG, PNG, GIF')}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label={translate('dm_template_configuration.default_action_url', 'Default Action URL')}
                  value={template.default_action.url}
                  onChange={(e) => handleDefaultActionChange('url', e.target.value)}
                  placeholder={translate('dm_template_configuration.enter_default_action_url', 'Enter default action URL (e.g., https://example.com)')}
                  type="url"
                  helperText={translate('dm_template_configuration.opens_when_card_tapped', 'Opens when card is tapped')}
                />
              </Grid>
              <Grid item xs={12}>
                {template?.buttons?.length > 0 && <>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, fontSize: '1rem' }}>
                  {translate('dm_template_configuration.buttons_max_3', 'Buttons (Max 3)')}
                </Typography>
                {template.buttons.map((button, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      boxShadow: 1,
                    }}
                  >
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12}>
                        <StyledSelect
                          fullWidth
                          size="small"
                          value={button.type}
                          onChange={(e) => handleButtonChange(index, 'type', e.target.value)}
                        >
                          <MenuItem value="web_url">{translate('dm_template_configuration.web_url', 'Web URL')}</MenuItem>
                          <MenuItem value="postback">{translate('dm_template_configuration.postback', 'Postback')}</MenuItem>
                        </StyledSelect>
                      </Grid>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label={translate('dm_template_configuration.button_title', 'Button Title')}
                          value={button.title}
                          onChange={(e) => handleButtonChange(index, 'title', e.target.value)}
                          placeholder={translate('dm_template_configuration.enter_button_title', 'Enter button title')}
                          inputProps={{ maxLength: 20 }}
                          helperText={button.type === 'web_url' ? `${button.title.length}/20` : `${button.title.length}/20 ${translate('dm_template_configuration.eg_chat_start', 'e.g., CHAT_START')}`}
                        />
                      </Grid>
                     {button.type === 'web_url'  && <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label={button.type === 'web_url' ? translate('dm_template_configuration.url', 'URL') : translate('dm_template_configuration.payload', 'Payload')}
                          value={button.type === 'web_url' ? button.url : button.payload}
                          onChange={(e) => handleButtonChange(index, button.type === 'web_url' ? 'url' : 'payload', e.target.value)}
                          placeholder={button.type === 'web_url' ? translate('dm_template_configuration.enter_url', 'Enter URL') : translate('dm_template_configuration.enter_payload', 'Enter payload')}
                          type={button.type === 'web_url' ? 'url' : 'text'}
                          helperText={button.type === 'web_url' ? translate('dm_template_configuration.eg_url', 'e.g., https://example.com') : translate('dm_template_configuration.eg_chat_start', 'e.g., CHAT_START')}
                        />
                      </Grid>}
                      <Grid item xs={12}>
                        <IconButton
                          onClick={() => removeButton(index)}
                          color="error"
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                {template.buttons.length < 3 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={addButton}
                    sx={{ mt: 1, borderRadius: 20, textTransform: 'none' }}
                  >
                    {translate('dm_template_configuration.add_button', 'Add Button')}
                  </Button>
                )}
                </>}
              </Grid>
            </Grid>
          </FormContainer>
        </Grid>
        <Grid item lg={12} xl={6}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 2, height: '100%' }}>
            <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
              {translate('dm_template_configuration.template_preview', 'Template Preview')}
            </Typography>
            {renderPreview()}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DMTemplateConfiguration;
